import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { basicPageProps } from '@/utils/isServer';
import { GetServerSideProps } from 'next';
import { Breadcrumb, Button, Input, Rate, Select } from 'antd';
import { modalType } from '@/components/Modal';
import SearchStore from '@/stores/SearchStore';
import { useMobxStores } from '@/stores';
import { pushAutoEvent } from '@/utils/gtag';
import { ArrowRightOutlined, CarOutlined, SafetyCertificateOutlined, StarFilled, TeamOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { CDN2 } from '@/helpers/api';
import { cacheFetch } from '@/helpers/cacheFetch';
import LongestLinesList2 from '@/components2/NewTSA/LongestLinesList2';
import { AirportDirectory } from '@/components2/NewTSA/AirportDirectory';
import Link from 'next/link';
import { airportsTSA } from '@/utils/isTSA';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { configInfo, airports } = await basicPageProps(ctx);
  const tsadata = await cacheFetch(
    `${process.env.FORCE_BLOG_ADDRESS}/blog/tsa/info.json`
  )

  return {
    props: {
      airports,
      configInfo,
      tsadata,
    },
  };
};


const airportAliases = {
  DEN: 'DIA',
};

const stats = [
  { icon: TeamOutlined, value: "2M+", label: "travelers served" },
  { icon: SafetyCertificateOutlined, value: "4.8/5", label: "average rating" },
  { icon: StarFilled, value: "70%", label: "avg savings" },
];

export default function TsaList({
  airports,
  tsadata, }) {
  const [travelersBooked, setTravelersBooked] = useState(43);

  useEffect(() => {
    const getRandomTravelers = () => Math.floor(Math.random() * 61) + 20;

    const interval = setInterval(() => {
      setTravelersBooked(getRandomTravelers());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const airportName = (code: string) => {
    const codeWithDIA = airportAliases[code] || code;

    const airportName = airports.result.find(
      (airport) =>
        airport.airport_initials.toLowerCase() == codeWithDIA.toLowerCase()
    );
    return airportName ? airportName?.airport_name : (tsadata[code]?.airport_name || code);
  };

  const airportDirectoryAirports = airportsTSA.map(({ code, region }) => ({
    code,
    region,
    name: airportName(code),
    mins: tsadata[code]?.wait_time ?? 0,
  }));

  return (
    <div className="min-h-screen relative bg-[#FCFCFC]">
      <Head>
        <title>Airport Security Wait Times | Real-Time TSA Line Updates</title>
        <meta
          name="description"
          content="Check real-time airport security wait times for major U.S. airports. Plan ahead and breeze through TSA with up-to-date wait time data."
          key="description"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.onairparking.com/"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "TSA Security Wait Times",
                  item: `https://www.onairparking.com/airport-security-wait-times/`
                }
              ]
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'LocalBusiness',
              name: 'On Air Parking',
              url: 'https://www.onairparking.com/',
              logo: CDN2() + '/staticmyapp/oglogo-white.png',
              description:
                'Unlock exclusive airport parking deals and save on your parking reservations.',
            }
            )
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'TravelAction',
              name: 'Reserve Airport Parking',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://www.onairparking.com/airports',
                inLanguage: 'en-US',
                actionPlatform: [
                  'http://schema.org/DesktopWebPlatform',
                  'http://schema.org/MobileWebPlatform'
                ]
              },
              result: {
                '@type': 'Reservation',
                reservationStatus: 'https://schema.org/ReservationConfirmed'
              }
            })
          }}
        />
      </Head>

      <div style={{ backgroundImage: 'linear-gradient(to bottom, #FBF5F1, #FCFCFC)' }}>
        <div className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
          <section className="p-5 md:py-10 justify-self-center" >
            <div className="container text-center max-w-2xl px-5">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary mb-2 animate-pulse">
                <ThunderboltOutlined />
                🔥 {travelersBooked} travelers booked parking in the last hour
              </div>
              <h1 className="font-display text-2xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
                TSA Lines Are Long — <br className="hidden md:block" />Don't Waste Time on Parking
              </h1>
              <p className="mt-2 text-sm md:text-lg max-w-xl mx-auto">
                Pre-book off-site parking and <strong className="text-foreground">save up to 70%</strong>. Free shuttle. Free cancellation.
              </p>
              <Link
                type="link"
                href="/airports"
                onClick={() => {
                  pushAutoEvent("tsa", 'NewTSA-goToAirportsPage', "click");
                }}
              >
                <Button type="primary" size="large" className="mt-4 gap-2 h-14 md:h-12 text-base w-full md:w-auto shadow-lg shadow-primary/25 rounded-xl">
                  <CarOutlined /> Book Parking Now — Save 70% <ArrowRightOutlined />
                </Button>
              </Link>
              <Breadcrumb className="justify-self-center text-sm md:text-base mt-4 mb-8">
                <Breadcrumb.Item>
                  <a onClick={() => pushAutoEvent("tsa", 'NewTSA-goToHome', "click")} href="/">Home</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  Security Wait Times
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </section>
        </div>
      </div>

      <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="text-center rounded-xl border border-border/50 py-3 px-2 bg-gray-50">
              <s.icon className="text-base text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-foreground font-display">{s.value}</p>
              <p className="text-[10px]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <LongestLinesList2 airports={tsadata} />

      <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4" >
        <div className="flex md:grid md:grid-cols-3 gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide -mx-1 px-1">
          {[
            { text: "Booked ATL parking in 30 seconds. Saved $85!", author: "Emily R." },
            { text: "The shuttle was waiting for us. Fastest airport experience ever.", author: "Marcus T." },
            { text: "Why did I ever pay airport prices? This is a no-brainer.", author: "Sarah L." },
          ].map((r) => (
            <div key={r.author} className="snap-start shrink-0 w-[80vw] md:w-auto rounded-xl border border-border bg-white p-4">
              <Rate disabled defaultValue={5} className="text-sm !text-primary mb-2" />
              <p className="text-sm text-foreground italic">"{r.text}"</p>
              <p className="mt-2 text-xs font-medium">— {r.author}</p>
            </div>
          ))}
        </div>

        <Link
          type="link"
          href={`/parking-coupons`}
          onClick={() => {
            pushAutoEvent("tsa", 'NewTSA-goToParkingCoupons', "click");
          }}
        >
          <Button type="primary" size="large" className="w-full md:w-auto md:mx-auto md:flex text-base shadow-lg shadow-primary/20 mt-4 pt-2 rounded-xl">
            Book Discounted Parking Now <ArrowRightOutlined className="pt-1" />
          </Button>
        </Link>
      </section>


      <AirportDirectory airports={airportDirectoryAirports} />
      <h2 className="text-sm text-gray-600 font-medium text-center pb-4">
        *Based off of live <Link onClick={() => pushAutoEvent("tsa", 'NewTSA-goToTSAWebsite', "click")} href='https://tsawaittimes.com/' type="link" className='underline text-primary'>TSA</Link> data.
      </h2>
    </div >
  );
}